import React, { useEffect } from 'react'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  MenuItem,
} from '@material-ui/core'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../../dialog/Dialog'
import { FormValues } from './form-values'
import { ControlledTextField } from '../../form/ControlledTextField'
import { validators } from '../../../validation/validators'
import { BuildingType } from '../../../generated/graphql'
import { theme } from '../../../theme'
import { FormGroup } from '../../form/FormGroup'

type Props = {
  isOpen: boolean
  isCreate?: boolean
  defaultValues?: FormValues
  onClose: () => void
  onSubmit: (values: FormValues) => void
}

export function BuildingEnergyModelsDialog({
  isOpen,
  isCreate,
  defaultValues,
  onClose,
  onSubmit,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, handleSubmit, errors, reset } = useForm<
    Pick<FormValues, 'name' | 'type' | 'area'>
  >({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  return (
    <Dialog
      title={
        isCreate
          ? t('buildingEnergyModels.createNew')
          : defaultValues?.name ?? ''
      }
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="name"
              error={errors.name}
              label={t('buildingEnergyModels.dialog.name')}
              rules={validators.required}
              type="name"
            />
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="type"
              label={t('buildingEnergyModels.dialog.type')}
              rules={validators.required}
              select
            >
              {Object.values(BuildingType).map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`buildingType.${type}`)}
                </MenuItem>
              ))}
            </ControlledTextField>
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="area"
              error={errors.area}
              label={t('buildingEnergyModels.dialog.area')}
              rules={{ ...validators.required, ...validators.decimal }}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box flexGrow={1} />
            <Button variant="contained" onClick={onClose}>
              {t('common.cancel')}
            </Button>

            <Button type="submit" variant="contained" color="primary">
              {isCreate ? t('common.create') : t('common.saveChanges')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
